import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Image, Modal, Space, Spin, Table, Tag, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import {
    HOME_BANNERS_ROUTES,
    getHomeBannerEditUrl,
    getHomeBannerIconUrl,
} from '../home-banners/constants';
import { deleteHomeBanner, getHomeBanners } from './actions';
import { formatDateLabel, formatDateTimeLabel, isBannerActive } from './helpers';
import { HomeBanner } from './types';
import './home-banners-admin.scss';

const getBannerStatus = (banner: HomeBanner) => {
    const isActive = isBannerActive(banner);

    return <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Активно' : 'Неактивно'}</Tag>;
};

const HomeBannersAdmin = () => {
    const [items, setItems] = useState<HomeBanner[]>([]);
    const [loading, setLoading] = useState(false);

    const loadItems = useCallback(() => {
        setLoading(true);

        getHomeBanners()
            .then(({ data }) => setItems(data.content || []))
            .catch((error) => {
                console.error(error);
                message.error('Не удалось загрузить баннеры');
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const handleDelete = useCallback((item: HomeBanner) => {
        Modal.confirm({
            title: 'Удалить баннер?',
            content: item.titleRu,
            okText: 'Удалить',
            okButtonProps: { danger: true },
            cancelText: 'Отмена',
            onOk: async () => {
                try {
                    await deleteHomeBanner(item.id);
                    message.success('Баннер удалён');
                    loadItems();
                } catch (error) {
                    console.error(error);
                    message.error('Не удалось удалить баннер');
                }
            },
        });
    }, [loadItems]);

    const handleCreate = useCallback(() => {
        window.location.href = HOME_BANNERS_ROUTES.create;
    }, []);

    const handleEdit = useCallback((id: string) => {
        window.location.href = getHomeBannerEditUrl(id);
    }, []);

    const columns = useMemo<ColumnsType<HomeBanner>>(() => [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 90,
            align: 'center',
            render: (id: string) => id?.slice(0, 8),
        },
        {
            title: 'Изображение',
            dataIndex: 'imageId',
            width: 130,
            align: 'center',
            render: (imageId: string) =>
                imageId ? (
                    <Image
                        src={getHomeBannerIconUrl(imageId)}
                        width={96}
                        height={54}
                        className="home-banners-admin__table-image"
                    />
                ) : (
                    '-'
                ),
        },
        {
            title: 'Заголовок RU',
            dataIndex: 'titleRu',
            key: 'titleRu',
            align: 'center',
        },
        {
            title: 'Период публикации',
            key: 'period',
            align: 'center',
            render: (_, item) => (
                <span className="home-banners-admin__nowrap">
                    {formatDateLabel(item.publishStartDate)} - {formatDateLabel(item.publishEndDate)}
                </span>
            ),
        },
        {
            title: 'Статус',
            key: 'status',
            align: 'center',
            render: (_, item) => getBannerStatus(item),
        },
        {
            title: 'Порядок',
            dataIndex: 'displayOrder',
            width: 100,
            align: 'center',
        },
        {
            title: 'Изменён',
            dataIndex: 'updatedAt',
            width: 150,
            align: 'center',
            render: (value) => (
                <span className="home-banners-admin__nowrap">
                    {formatDateTimeLabel(value)}
                </span>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            align: 'center',
            render: (_, item) => (
                <Space
                    size={8}
                    className="home-banners-admin__actions"
                >
                    <Tooltip title="Редактировать">
                        <Button
                            type="text"
                            shape="circle"
                            icon={<EditOutlined />}
                            aria-label="Редактировать баннер"
                            className="home-banners-admin__action home-banners-admin__action--edit"
                            onClick={() => handleEdit(item.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Удалить">
                        <Button
                            type="text"
                            shape="circle"
                            icon={<DeleteOutlined />}
                            aria-label="Удалить баннер"
                            className="home-banners-admin__action home-banners-admin__action--delete"
                            onClick={() => handleDelete(item)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ], [handleDelete, handleEdit]);

    return (
        <div className="home-banners-admin">
            <div className="home-banners-admin__header">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Создать баннер
                </Button>
            </div>

            <Spin spinning={loading}>
                <Table<HomeBanner>
                    rowKey="id"
                    dataSource={items}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: true }}
                />
            </Spin>
        </div>
    );
};

export default HomeBannersAdmin;
